import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCommonTextFilterParams,
  getSetFilterParams,
  getSetDatefilterParams,
} from '../../utils/agGridFilters';
import DeleteConfirmationModal from '../../shared/modal/deleteConfirmation';
import { DateFormatter, DateTimeFormatter } from '../../shared/controls/formatters';
import { currencyFormatterByField } from '../../shared/constants';
import { getUserRole } from '../../redux/selector';
import { useSelector } from 'react-redux';

const OptionButton = ({
  handleClickOpenEditForm,
  paramsValue,
  handleOpenViewForm,
  rowData,
  handleClickDelete,
}) => {
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const handleDeleteClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmDelete = () => {
    handleClickDelete(paramsValue);
    setShowConfirmation(false);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  const userRole = useSelector(getUserRole)
      const role = userRole || '';
      const isSystemManager = role === 'System Manager';

   const isDisabled =
    rowData?.status && ['delivered', 'cancelled'].includes(rowData.status.toLowerCase());

  return (
    <div className="action-menu">
      {isDisabled && !isSystemManager ? (
      <img
        src="/assets/images/edit.png"
        style={{ filter: 'grayscale(100%)', opacity: 0.5, cursor: 'auto' }}
        className="action-img1"
        alt="Edit"
        title="Edit"
      ></img>
      ):(
        <img
        src="/assets/images/edit.png"
        className="action-img1"
        onClick={() => handleClickOpenEditForm(paramsValue)}
        alt="Edit"
        title="Edit"
      ></img>
      )}

      <img
        src="/assets/images/view.png"
        className="action-img2"
        onClick={() => handleOpenViewForm(paramsValue)}
        alt="View"
        title="View"
      />
{isDisabled && !isSystemManager ? (
      <img
        src="/assets/images/trash.png"
        style={{ filter: 'grayscale(100%)', opacity: 0.5, cursor: 'auto' }}
        className="action-img2"
        alt="Delete"
        title="Delete"
      ></img>
):(
  <img
        src="/assets/images/trash.png"
        className="action-img2"
        onClick={handleDeleteClick}
        alt="Delete"
        title="Delete"
      ></img>
)}
      <DeleteConfirmationModal
        show={showConfirmation}
        handleClose={handleCloseConfirmation}
        handleConfirm={handleConfirmDelete}
        rowData={rowData}
      />
    </div>
  );
};

const useSalesColumnDefs = ({ salesList, gridData, handleClickDelete, owner }) => {
  const navigate = useNavigate();
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);
  const filterParams = useMemo(() => getSetFilterParams, []);

  const handleClickOpenEditForm = (paramsValue) => {
    navigate(`/sales/editSalesOrder?id=${paramsValue.id}`, {
      state: paramsValue,
    });
  };

  const handleOpenViewForm = (paramsValue) => {
    navigate(
      `/sales/viewSalesOrder?id=${paramsValue.id}&pageNo=${gridData?.inputPayload?.pageNumber}`,
      {
        state: paramsValue,
      }
    );
  };

  const actionsColumn = useMemo(() => {
    return [
      {
        headerName: 'Actions',
        cellRenderer: (params) => {
          const rowData = { ...params.data, rowIndex: params.rowIndex };
          return (
            <OptionButton
              rowData={rowData}
              handleClickOpenEditForm={() => handleClickOpenEditForm(rowData)}
              handleClickDelete={() => handleClickDelete(rowData)}
              handleOpenViewForm={() => handleOpenViewForm(rowData)}
            />
          );
        },
        maxWidth: 100,
        minWidth: 100,
        sortable: false,
        filter: false,
        pinned: 'left',
      },
    ];
  }, [handleClickOpenEditForm, handleClickDelete]);

  const columnDefs = useMemo(() => {
    return [
      ...actionsColumn,
      {
        headerName: 'Code',
        field: 'code',
        width: 150,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Subject',
        field: 'subject',
        width: 150,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 120,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Grand Total',
        field: 'grandTotal',
        width: 130,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Opportunity Name',
        field: 'opportunityName',
        width: 150,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Contact Name',
        field: 'contactName',
        width: 140,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Account Name',
        field: 'accountName',
        width: 140,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Sales Order Owner',
        field: 'salesOwner',
        width: 180,
        filter: 'agSetColumnFilter',
        filterParams: filterParams(owner?.map((item) => item.display_name)),
      },
      // {
      //   headerName: 'Sales Order Number',
      //   field: 'salesOrderNumber',
      //   width: 180,
      //   filter: 'agNumberColumnFilter',
      //   filterParams: commonTextFilterParams,
      // },
      {
        headerName: 'Sales Order Date',
        field: 'orderDate',
        width: 150,
        valueFormatter: DateFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Due Date',
        field: 'dueDate',
        width: 130,
        valueFormatter: DateFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Created Date Time',
        field: 'createdDateTime',
        width: 150,
        valueFormatter: DateTimeFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Currency',
        field: 'currency',
        width: 100,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        // valueFormatter: currencyFormatterByField('currency'),
      },
      {
        headerName: 'Quote Name',
        field: 'quoteName',
        width: 130,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Carrier',
        field: 'carrier',
        width: 120,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
    ];
  }, [actionsColumn, commonTextFilterParams, filterParams, salesList, gridData, dateFilterParams, owner]);

  return columnDefs;
};

export default useSalesColumnDefs;
